import React, { useState, useEffect } from 'react';
import { 
  DollarSign,
  TrendingUp,
  TrendingDown,
  PieChart,
  Loader2,
  RefreshCw,
  AlertTriangle,
  CheckCircle
} from 'lucide-react';
import { apiRequest } from '../../lib/utils';
import { Button } from '../ui/button';

export default function MarketingBudgetWidget({ showHeader = true }) {
  const [budget, setBudget] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBudget();
  }, []);

  const fetchBudget = async () => {
    try {
      const data = await apiRequest('/sales/marketing-budget');
      setBudget(data || {});
    } catch (error) {
      console.error('Failed to fetch budget:', error);
      setBudget(getMockBudget());
    } finally {
      setLoading(false);
    }
  };

  const spentPercentage = budget.total ? ((budget.spent / budget.total) * 100).toFixed(0) : 0;
  const isOverBudget = spentPercentage > 100;
  const isNearLimit = spentPercentage > 80 && !isOverBudget;

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm h-full flex flex-col" data-testid="marketing-budget-widget">
      {showHeader && (
        <div className="p-4 border-b border-slate-100 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
              <DollarSign size={20} className="text-white" />
            </div>
            <div>
              <h3 className="font-semibold text-slate-900">Marketing Budget</h3>
              <p className="text-xs text-slate-500">{budget.period || 'This Month'}</p>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={fetchBudget}>
            <RefreshCw size={16} />
          </Button>
        </div>
      )}

      {/* Budget Overview */}
      <div className="p-4 border-b border-slate-100">
        <div className="flex items-center justify-between mb-3">
          <div>
            <p className="text-3xl font-bold text-slate-900">${budget.spent?.toLocaleString()}</p>
            <p className="text-sm text-slate-500">of ${budget.total?.toLocaleString()} budget</p>
          </div>
          <div className={`w-16 h-16 rounded-full flex items-center justify-center ${
            isOverBudget ? 'bg-red-100' : isNearLimit ? 'bg-yellow-100' : 'bg-green-100'
          }`}>
            {isOverBudget ? (
              <AlertTriangle size={24} className="text-red-600" />
            ) : isNearLimit ? (
              <AlertTriangle size={24} className="text-yellow-600" />
            ) : (
              <CheckCircle size={24} className="text-green-600" />
            )}
          </div>
        </div>
        
        {/* Progress Bar */}
        <div className="h-3 bg-slate-100 rounded-full overflow-hidden">
          <div 
            className={`h-full rounded-full transition-all ${
              isOverBudget ? 'bg-red-500' : isNearLimit ? 'bg-yellow-500' : 'bg-green-500'
            }`}
            style={{ width: `${Math.min(spentPercentage, 100)}%` }}
          />
        </div>
        <div className="flex items-center justify-between mt-2">
          <span className="text-xs text-slate-500">{spentPercentage}% used</span>
          <span className="text-xs text-slate-500">${(budget.total - budget.spent).toLocaleString()} remaining</span>
        </div>
      </div>

      {/* Breakdown by Channel */}
      <div className="flex-1 overflow-y-auto p-4">
        <p className="text-xs font-medium text-slate-500 mb-3">SPEND BY CHANNEL</p>
        <div className="space-y-3">
          {(budget.channels || []).map((channel, idx) => {
            const channelPercent = budget.spent ? ((channel.spent / budget.spent) * 100).toFixed(0) : 0;
            return (
              <div key={idx}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium text-slate-700">{channel.name}</span>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-slate-600">${channel.spent?.toLocaleString()}</span>
                    <span className={`text-xs font-medium ${channel.roi >= 100 ? 'text-green-600' : 'text-red-600'}`}>
                      {channel.roi}% ROI
                    </span>
                  </div>
                </div>
                <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-gradient-to-r from-teal-400 to-emerald-500 rounded-full"
                    style={{ width: `${channelPercent}%` }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Quick Stats Footer */}
      <div className="p-3 border-t border-slate-100 bg-slate-50/50">
        <div className="grid grid-cols-2 gap-2 text-center">
          <div>
            <p className="text-lg font-bold text-slate-900">${budget.cpa?.toFixed(2)}</p>
            <p className="text-xs text-slate-500">Cost per Acquisition</p>
          </div>
          <div>
            <p className={`text-lg font-bold ${budget.overall_roi >= 100 ? 'text-green-600' : 'text-red-600'}`}>
              {budget.overall_roi}%
            </p>
            <p className="text-xs text-slate-500">Overall ROI</p>
          </div>
        </div>
      </div>
    </div>
  );
}

function getMockBudget() {
  return {
    total: 25000,
    spent: 18500,
    period: 'March 2026',
    cpa: 12.50,
    overall_roi: 245,
    channels: [
      { name: 'Social Media Ads', spent: 6500, roi: 280 },
      { name: 'Print Flyers', spent: 4200, roi: 195 },
      { name: 'In-Store Displays', spent: 3800, roi: 320 },
      { name: 'Email Marketing', spent: 2200, roi: 450 },
      { name: 'Influencer Partnerships', spent: 1800, roi: 185 }
    ]
  };
}
